import { FlexBox } from '@airship-platform/jam-web/components';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import Divider from '@mui/material/Divider';
import Link from '@mui/material/Link';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import {
  CustomerMedia,
  useAppointmentProposalFiles,
} from '../../../../data-access/appointmentProposalFiles';

interface FormValues {
  tempTitle: string;
  tempNote: string;
}

const MediaList: React.FC = () => {
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const { control, handleSubmit, setValue, reset } = useForm<FormValues>();

  const {
    files,
    isProcessing,
    uploadProgress,
    deleteCustomerFile,
    updateCustomerFile,
  } = useAppointmentProposalFiles();

  const handleDelete = (fileId: string) => deleteCustomerFile(fileId);

  const existemSystemFiles = files.filter(
    (file) =>
      !file.metadata.uploadCategory ||
      ['EXISTING_SYSTEM', 'JOB_ATTACHMENT'].includes(
        file.metadata.uploadCategory
      )
  );

  const handleAddNote = (index: number) => {
    setEditIndex(index);
    setValue('tempNote', existemSystemFiles[index].notes || '');
    setValue('tempTitle', existemSystemFiles[index].title || '');
  };

  const handleSaveNote = (data: FormValues) => {
    if (editIndex !== null) {
      updateCustomerFile(
        existemSystemFiles[editIndex].customerFileId,
        data.tempTitle,
        data.tempNote
      );
    }
    setEditIndex(null);
    reset();
  };

  const renderMedia = (file: CustomerMedia) =>
    file.contentType.startsWith('video') ? (
      <Box
        component="video"
        src={file.url}
        sx={{
          width: '7.6rem',
          height: '10rem',
          objectFit: 'contain',
        }}
      />
    ) : (
      <Box
        component="img"
        src={file.url}
        alt={'Preview'}
        sx={{
          width: '7.6rem',
          height: '10rem',
          objectFit: 'contain',
        }}
      />
    );

  return (
    <FlexBox flexDirection={'column'} gap={2} width={'100%'}>
      {existemSystemFiles.map((file, index) => (
        <FlexBox
          key={file.customerFileId}
          flexDirection={'column'}
          alignItems={'center'}
          gap={3}
        >
          <Divider flexItem />
          <FlexBox
            display={'flex'}
            flexDirection={'column'}
            gap={2}
            width={'100%'}
          >
            <Typography variant={'h4'}>
              {file.title ? file.title : `Photo ${index + 1}`}
            </Typography>
            <FlexBox
              gap={2}
              alignItems={'center'}
              width={'100%'}
              justifyContent={'space-between'}
            >
              <FlexBox flexDirection={'column'} gap={2}>
                {renderMedia(file)}

                {editIndex !== index && !file.notes && (
                  <Button
                    color="softBlue"
                    disabled={isProcessing.includes(file.customerFileId)}
                    onClick={() => handleAddNote(index)}
                  >
                    Add Note
                  </Button>
                )}
              </FlexBox>
              <FlexBox flexDirection={'column'} alignItems={'center'} gap={2}>
                {isProcessing.includes(file.customerFileId) &&
                  uploadProgress[file.customerFileId] < 100 && (
                    <CircularProgress
                      size={24}
                      variant="determinate"
                      value={uploadProgress[file.customerFileId]}
                      onClick={() =>
                        file.uploadAbort && file.uploadAbort.abort()
                      }
                    />
                  )}
                <Button
                  variant="text"
                  color="blue"
                  disabled={isProcessing.includes(file.customerFileId)}
                  onClick={() => handleDelete(file.customerFileId)}
                >
                  Remove
                </Button>
              </FlexBox>
            </FlexBox>
            {file.notes && (
              <FlexBox
                justifyContent="space-between"
                alignItems="center"
                maxWidth="25rem"
                gap={2}
              >
                <FlexBox flexDirection="column">
                  <Typography variant="body2">Note</Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      wordBreak: 'break-word',
                    }}
                  >
                    {file.notes}
                  </Typography>
                </FlexBox>
                <Link variant="body2" onClick={() => handleAddNote(index)}>
                  Edit
                </Link>
              </FlexBox>
            )}
            {editIndex === index && (
              <Dialog
                open={editIndex === index}
                onClose={() => setEditIndex(null)}
              >
                <FlexBox flexDirection={'column'} width={'100%'} p={4}>
                  <Typography variant={'h4'}>Edit Note</Typography>
                  <form
                    onSubmit={(e) => {
                      e.stopPropagation();
                      handleSubmit(handleSaveNote)(e);
                    }}
                  >
                    <FlexBox flexDirection={'column'} gap={2} mt={2}>
                      <Controller
                        name="tempTitle"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            label="Photo title"
                            variant="outlined"
                            fullWidth
                            {...field}
                          />
                        )}
                      />
                      <Controller
                        name="tempNote"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            label="Add Note"
                            variant="outlined"
                            fullWidth
                            multiline
                            rows={4}
                            {...field}
                          />
                        )}
                      />
                      <FlexBox gap={2} justifyContent={'space-between'}>
                        <Button
                          color={'softGold'}
                          onClick={() => {
                            setEditIndex(null);
                            reset();
                          }}
                        >
                          Cancel
                        </Button>
                        <Button type="submit">Save note</Button>
                      </FlexBox>
                    </FlexBox>
                  </form>
                </FlexBox>
              </Dialog>
            )}
          </FlexBox>
        </FlexBox>
      ))}
    </FlexBox>
  );
};

export default MediaList;
